import { Spinner } from '../../components/Spinner.js';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { getCategoryLabel } from '@ridehub/shared';
import type { SpotCategory } from '@ridehub/shared';
import { CategoryIcon } from '../../components/CategoryIcon.js';
import { apiClient } from '../../lib/api-client.js';

interface BookmarkedSpot {
  id: string;
  spotId: string;
  createdAt: string;
  spot: {
    id: string;
    name: string;
    category: string;
    address: string | null;
    totalLikes: number;
    defaultPhotoUrl: string | null;
  };
}

function BookmarkIcon() {
  return (
    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" className="text-gray-300">
      <path
        d="M5 3a2 2 0 0 0-2 2v16l7-3 7 3V5a2 2 0 0 0-2-2H5z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function BookmarkListView() {
  const navigate = useNavigate();
  const [bookmarks, setBookmarks] = useState<BookmarkedSpot[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiClient
      .get<BookmarkedSpot[]>('/bookmarks', { requireAuth: true })
      .then((data) => setBookmarks(data ?? []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="max-w-2xl mx-auto">
      <div className="px-4 py-4 border-b border-gray-200">
        <h1 className="text-lg font-bold text-gray-900">ブックマーク</h1>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <Spinner />
        </div>
      ) : bookmarks.length === 0 ? (
        <div className="flex flex-col items-center gap-3 py-16 text-gray-400">
          <BookmarkIcon />
          <p className="text-sm">ブックマークしたスポットはありません</p>
        </div>
      ) : (
        <ul className="divide-y divide-gray-100">
          {bookmarks.map(({ id, spot }) => (
            <li key={id}>
              <button
                onClick={() => navigate(`/spots/${spot.id}`)}
                className="w-full text-left px-4 py-3 flex items-center gap-3 hover:bg-gray-50 active:bg-gray-100 transition-colors"
              >
                {spot.defaultPhotoUrl ? (
                  <img
                    src={spot.defaultPhotoUrl}
                    alt=""
                    className="w-14 h-14 object-cover rounded-lg flex-shrink-0"
                  />
                ) : (
                  <div className="w-14 h-14 bg-gray-100 rounded-lg flex-shrink-0 flex items-center justify-center">
                    <CategoryIcon category={spot.category as SpotCategory} size="md" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-900 truncate">{spot.name}</p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {getCategoryLabel(spot.category as SpotCategory)}
                  </p>
                  {spot.address && (
                    <p className="text-xs text-gray-400 truncate mt-0.5">{spot.address}</p>
                  )}
                </div>
                <div className="flex-shrink-0 text-xs text-gray-400 flex items-center gap-1">
                  <svg
                    width="11"
                    height="11"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                  </svg>
                  <span>{spot.totalLikes}</span>
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
